import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { api } from "../../lib/api";
import ApprovalPolicyPanel from "../../components/finance/ApprovalPolicyPanel";

export const usd = (c) => (c === null || c === undefined ? "—" : `${c < 0 ? "-" : ""}$${(Math.abs(c) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const BUCKET = { current: "Current", "1_30": "1–30", "31_60": "31–60", "61_90": "61–90", "90_plus": "90+" };
const msg = (e) => e?.response?.data?.detail?.message || "Could not load.";

export const download = (path, name) => api.get(path, { responseType: "blob" }).then((r) => {
  const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = name; a.click(); URL.revokeObjectURL(a.href);
});

function Aging({ kind }) {
  const [a, setA] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/finance/${kind}/aging`).then((r) => setA(r.data)).catch((e) => setErr(msg(e))); }, [kind]);
  if (err) return <p role="alert" className="text-sm text-red-700" data-testid={`fin-${kind}-error`}>{err}</p>;
  if (!a) return null;
  return (
    <section className="space-y-2" data-testid={`fin-${kind}-aging`}>
      <h2 className="font-heading text-lg">{kind === "ar" ? "Receivables aging" : "Payables aging"} · {usd(a.total_cents)}</h2>
      <div className="grid grid-cols-5 gap-2 text-sm">{Object.entries(a.buckets).map(([b, v]) => (
        <div key={b} className="border border-erf-borderCream rounded bg-white p-2" data-testid={`fin-${kind}-bucket-${b}`}><div className="text-xs">{BUCKET[b]}</div>{usd(v)}</div>))}</div>
      <button type="button" className="min-h-[44px] underline text-sm" onClick={() => download(`/finance/${kind}/aging.csv`, `${kind}_aging.csv`)} data-testid={`fin-${kind}-csv`}>Export CSV</button>
    </section>
  );
}

function Cash() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/finance/cash").then((r) => setC(r.data)).catch(() => setC(null)); }, []);
  if (!c) return null;
  return (
    <section className="space-y-1 border border-erf-borderCream rounded bg-white p-3" data-testid="fin-cash">
      <h2 className="font-heading text-lg">{c.label}: {usd(c.position_cents)}</h2>
      <p className="text-xs font-semibold" data-testid="fin-cash-note">{c.note}</p>
      <p className="text-sm">Recorded receipts {usd(c.recorded_receipts_cents)} − recorded disbursements {usd(c.recorded_disbursements_cents)}</p>
      <ul className="text-sm">{c.forecast.map((f) => <li key={f.through} data-testid={`fin-forecast-${f.through}`}>Through {f.through}: in {usd(f.expected_in_cents)}, out {usd(f.expected_out_cents)}, net {usd(f.net_cents)}</li>)}</ul>
    </section>
  );
}

export function JobCostCard({ j, name }) {
  const row = (label, v, id) => <div className="flex justify-between gap-2" data-testid={id}><span>{label}</span><span className="tabular-nums">{v}</span></div>;
  return (
    <section className="border border-erf-borderCream rounded bg-white p-3 text-sm space-y-1" data-testid={`fin-jc-${j.project_id}`}>
      <h3 className="font-semibold">{name} {j.over_budget && <span className="text-red-800" data-testid={`fin-jc-over-${j.project_id}`}>· over budget</span>}</h3>
      {row("Estimated cost", usd(j.estimated_cost_cents), `fin-jc-est-${j.project_id}`)}{row("Committed (POs + subcontracts)", usd(j.committed_cents))}
      {row("Actual (bills + vendor invoices + labor)", usd(j.actual_cents))}{row("Projected cost", usd(j.projected_cost_cents))}
      {row("Variance", usd(j.variance_cents), `fin-jc-var-${j.project_id}`)}{row("Change orders (approved)", usd(j.change_orders_cents))}
      {row("Revised contract", usd(j.revised_contract_cents))}{row("Gross profit", usd(j.gross_profit_cents))}
      {row("Margin", j.margin_pct === null ? "—" : `${j.margin_pct}%`, `fin-jc-margin-${j.project_id}`)}
      {j.actual_breakdown.trades_without_rate.length > 0 && <p className="text-xs">No labor rate for: {j.actual_breakdown.trades_without_rate.join(", ")} (hours excluded).</p>}
      <button type="button" className="min-h-[44px] underline" onClick={() => download(`/finance/projects/${j.project_id}/job-cost.csv`, `job_cost_${j.project_id}.csv`)} data-testid={`fin-jc-csv-${j.project_id}`}>Export CSV</button>
    </section>
  );
}

// B10: finance home (finance roles) — internal records only.
export default function FinanceHome() {
  const [jc, setJc] = useState([]);
  useEffect(() => { api.get("/finance/job-cost").then((r) => setJc(r.data)).catch(() => setJc([])); }, []);
  return (
    <AppShell title="Finance" subtitle="Internal records only — no bank, accounting or payment connection">
      <div className="mx-auto w-full max-w-[1000px] px-4 space-y-6" data-testid="fin-home">
        <ApprovalPolicyPanel /><Cash /><Aging kind="ar" /><Aging kind="ap" />
        <section className="space-y-2"><h2 className="font-heading text-lg">Job cost by project</h2>
          <div className="grid gap-3 md:grid-cols-2">{jc.map((j) => <JobCostCard key={j.project_id} j={j} name={j.project_name} />)}</div></section>
      </div>
    </AppShell>
  );
}

// PMs: their project's job cost only.
export function ProjectJobCost() {
  const { pid } = useParams();
  const [j, setJ] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/finance/projects/${pid}/job-cost`).then((r) => setJ(r.data)).catch((e) => setErr(msg(e))); }, [pid]);
  return (
    <AppShell title="Job cost" subtitle="Internal records only">
      <div className="mx-auto w-full max-w-[720px] px-4" data-testid="fin-project-jc">
        {err && <p role="alert" className="text-sm text-red-700" data-testid="fin-project-jc-error">{err}</p>}
        {j && <JobCostCard j={j} name="This project" />}
      </div>
    </AppShell>
  );
}
